import styled from "styled-components";
import { useUserContext } from "@contexts/UserContext";
import Styles from "@themes/Styles";
import { darkTheme, lightTheme } from "@themes/Theme";

const Backdrop = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    background-image: linear-gradient(#5b65f9, #202bcd);
    padding: 64px;
`;

const Title = styled.h1`
    text-align: center;
    color: ${darkTheme.textColorPrimary};
    font-size: 20px;
`;

const Container = styled.div`
    background: ${lightTheme.backgroundPrimary};
    color: ${lightTheme.textColorPrimary};
    border-radius: ${Styles.borderRadiusNormal};
    padding: 32px;
    display: flex;
    flex-direction: column;
    max-width: 480px;
`;

const ContainerHeader = styled.h2`
    font-size: 16px;
    font-weight: normal;
`;

const BigNumber = styled.span`
    font-size: 64px;
    font-weight: bold;
`;

const TimeEquivalent = styled.span`
    font-size: 14px;
    margin-bottom: 32px;
`;

const BreakdownHeader = styled.h2`
    font-size: 16px;
    margin-bottom: 12px;
`;

const BreakdownGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    font-size: 14px;
`;

const GridLabel = styled.span``;

const GridValue = styled.span`
    justify-self: end;
`;

const Divider = styled.div`
    grid-column: span 2;
    border-top: 1px solid ${lightTheme.borderPrimary};
`;

const BottomLineLabel = styled(GridLabel)`
    font-weight: bold;
`;

const BottomLineValue = styled(GridValue)`
    font-weight: bold;
`;

type Props = {
    subscriptionCost: number;
};

export function RoiCalculator(props: Props) {
    const { userData } = useUserContext();

    const hourlyRate = userData?.additionalUserSuppliedData?.hourlyRate ?? 20;
    const hoursLostWeekly = userData?.additionalUserSuppliedData?.hoursLostWeekly ?? 10;
    const hoursLostMonthly = hoursLostWeekly * 4;

    const valueOfTimeSaved = hoursLostMonthly * hourlyRate;
    const roi = valueOfTimeSaved - props.subscriptionCost;

    return (
        <Backdrop>
            <Title>Your ROI Results</Title>
            <Container>
                <ContainerHeader>Estimated return on investment per month with LifeAt</ContainerHeader>
                <BigNumber>${roi}</BigNumber>
                <TimeEquivalent>Or {hoursLostMonthly} hours of your life</TimeEquivalent>
                <BreakdownHeader>How did we get this number?</BreakdownHeader>
                <BreakdownGrid>
                    <GridLabel>Hours saved per month</GridLabel>
                    <GridValue>{hoursLostMonthly} hours</GridValue>
                    <GridLabel>Value of time saved (${hourlyRate}/hr)</GridLabel>
                    <GridValue>${valueOfTimeSaved}</GridValue>
                    <GridLabel>Monthly subscription cost</GridLabel>
                    <GridValue>${props.subscriptionCost}</GridValue>
                    <Divider />
                    <BottomLineLabel>Total ROI per month</BottomLineLabel>
                    <BottomLineValue>${roi}</BottomLineValue>
                </BreakdownGrid>
            </Container>
        </Backdrop>
    );
}
